import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { useAddon } from './useAddon';
import { ADDONS } from '../utils/addons';
import { usePermissions } from './usePermissions';
import { FEATURE_PERMISSIONS, canModifyData, canViewAllData } from '../utils/permissions';

/**
 * Quote builder availability (AI Sales Agent Phase 6).
 * - enabled: add-on on and the owner flag `quotingEnabled` on.
 * - canView: enabled and the user may read quotes (builders, admins, Admin - View Only).
 * - canBuild: enabled, `feature:quotes_build`, and not a read-only role.
 * - aiAvailable: canBuild plus the paid add-on and `quoteAiEnabled` + `llmEnabled`.
 * - canSeeTeam: may list every quote for a customer, not just their own.
 * Client gating is cosmetic; the callables and Firestore rules enforce the same checks.
 */
export function useQuoting(currentUser) {
  const { enabled: addonEnabled } = useAddon(ADDONS.AI_SALES_AGENT);
  const { enabled: paidEnabled } = useAddon(ADDONS.AI_SALES_AGENT_PAID);
  const { can } = usePermissions(currentUser);
  const [flags, setFlags] = useState({ loaded: false, quotingEnabled: false, quoteAiEnabled: false });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!addonEnabled) {
      setFlags({ loaded: true, quotingEnabled: false, quoteAiEnabled: false });
      setError('');
      return undefined;
    }
    const unsubscribe = onSnapshot(
      doc(db, collectionPath('config'), 'aiAgentSettings'),
      snap => {
        const data = snap.data() || {};
        setError('');
        setFlags({
          loaded: true,
          quotingEnabled: data.quotingEnabled === true,
          quoteAiEnabled: data.quoteAiEnabled === true && data.llmEnabled === true,
        });
      },
      err => {
        console.error('Failed to load quoting settings:', err.message);
        setError('Quote settings could not be loaded.');
        setFlags({ loaded: true, quotingEnabled: false, quoteAiEnabled: false });
      }
    );
    return unsubscribe;
  }, [addonEnabled]);

  const enabled = Boolean(addonEnabled && flags.quotingEnabled);
  const hasBuild = can(FEATURE_PERMISSIONS.QUOTES_BUILD);
  const allData = canViewAllData(currentUser);
  const canBuild = enabled && hasBuild && canModifyData(currentUser);
  return {
    loaded: flags.loaded,
    addonEnabled,
    error,
    enabled,
    canView: enabled && (hasBuild || allData),
    canBuild,
    canSeeTeam: allData || (hasBuild && can(FEATURE_PERMISSIONS.AI_RECS_VIEW_TEAM)),
    aiAvailable: canBuild && paidEnabled && flags.quoteAiEnabled,
  };
}